import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Icon,
  Badge,
  List,
  ListItem,
  Flex,
  Button
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import {
  platformCards,
  includedDashboards,
  platformGuarantees
} from './marketingContent';
import MarketingFooter from './MarketingFooter';
import { marketingHeadingStyles } from './marketingStyles';

const PlatformsPage = () => {
  return (
    <Box fontFamily="var(--telivi-font-body)" sx={marketingHeadingStyles}>
      <Box bgGradient="linear(to-b, gray.50, white)" pt={{ base: 16, lg: 24 }} pb={{ base: 12, lg: 16 }}>
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center">
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Platforms
            </Text>
            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }} fontFamily="var(--telivi-font-heading)">
              Choose your core platform
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Every Telivi platform runs on isolated infrastructure with shared operational
              dashboards already included.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {platformCards.map((platform) => (
              <Box
                key={platform.id}
                id={platform.id}
                bg="white"
                borderRadius="2xl"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.100"
                p={8}
              >
                <Stack spacing={4}>
                  <Flex align="center" gap={3}>
                    <Flex
                      align="center"
                      justify="center"
                      bg="blue.50"
                      color="blue.600"
                      borderRadius="lg"
                      w={12}
                      h={12}
                    >
                      <Icon as={platform.icon} boxSize={6} />
                    </Flex>
                    <Heading as="h2" fontSize="xl">
                      {platform.title}
                    </Heading>
                  </Flex>
                  <Text color="gray.600">{platform.description}</Text>
                  <Box>
                    <Badge colorScheme="blue" mb={2}>
                      Core Service Dashboard
                    </Badge>
                    <Text fontWeight="600">{platform.coreService}</Text>
                  </Box>
                  <Box>
                    <Badge colorScheme="gray" mb={2}>
                      Included Dashboards
                    </Badge>
                    <List spacing={2} color="gray.600">
                      {includedDashboards
                        .filter((dashboard) => dashboard.title !== 'Core Service Dashboard')
                        .map((dashboard) => (
                          <ListItem key={dashboard.title}>
                            <Flex align="center" gap={2}>
                              <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                              <Text>{dashboard.title}</Text>
                            </Flex>
                          </ListItem>
                        ))}
                    </List>
                  </Box>
                  <Button as={RouterLink} to="/pricing" colorScheme="blue" variant="outline">
                    View pricing
                  </Button>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center" mb={10}>
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Included Dashboards
            </Text>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
              A complete operating suite for every platform
            </Heading>
          </Stack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {includedDashboards.map((dashboard) => (
              <Box
                key={dashboard.title}
                bg="white"
                borderRadius="2xl"
                p={6}
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <Flex align="center" gap={3} mb={3}>
                  <Flex
                    align="center"
                    justify="center"
                    bg="orange.50"
                    color="orange.500"
                    borderRadius="lg"
                    w={10}
                    h={10}
                  >
                    <Icon as={dashboard.icon} />
                  </Flex>
                  <Heading as="h3" fontSize="lg">
                    {dashboard.title}
                  </Heading>
                </Flex>
                <Text color="gray.600">{dashboard.description}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center" mb={8}>
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Platform Guarantees
            </Text>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
              Isolation is built in, not bolted on
            </Heading>
          </Stack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {platformGuarantees.map((guarantee) => (
              <Flex
                key={guarantee.title}
                align="center"
                gap={3}
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.100"
                px={4}
                py={4}
                boxShadow="sm"
              >
                <Icon as={guarantee.icon} color="blue.500" boxSize={5} />
                <Text fontWeight="600">{guarantee.title}</Text>
              </Flex>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default PlatformsPage;
